import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, arrayUnion, collection, query, where, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import Card from "../../components/Card";
import { AppUser } from "../../types/user";
import { Lightbulb, CheckCircle, Smartphone, ClipboardList, AlertCircle, CreditCard, Tag, Check, X } from "lucide-react";

export default function CourseCheckoutQR() {
  const { courseId } = useParams<{ courseId: string }>();
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [course, setCourse] = useState<any>(null);
  const [instructor, setInstructor] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  
  // Discount states
  const [discountCode, setDiscountCode] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState<any>(null);
  const [discountError, setDiscountError] = useState("");
  const [checkingDiscount, setCheckingDiscount] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);

  const themeColor = instructor?.themeColor || instructor?.buttonColor || "#8B5CF6";
  const headingColor = instructor?.headingColor || "#1F2937";
  const buttonColor = instructor?.buttonColor || instructor?.themeColor || "#8B5CF6";

  console.log('CourseCheckoutQR - Instructor:', instructor);
  console.log('CourseCheckoutQR - Colors:', { themeColor, headingColor, buttonColor });

  useEffect(() => {
    const fetchData = async () => {
      if (!courseId) return;

      try {
        // Load course
        const courseRef = doc(db, "courses", courseId);
        const courseSnap = await getDoc(courseRef);

        if (courseSnap.exists()) {
          const courseData = { id: courseSnap.id, ...courseSnap.data() };
          setCourse(courseData);
          setFinalPrice(courseData.price || 0);

          // Load instructor
          const instructorRef = doc(db, "users", courseData.instructorId);
          const instructorSnap = await getDoc(instructorRef);

          if (instructorSnap.exists()) {
            const instructorData = { uid: instructorSnap.id, ...instructorSnap.data() } as AppUser;
            console.log('🔍 Instructor Data:', instructorData);
            console.log('💳 Payment QR Code:', instructorData.paymentQRCode);
            console.log('🏦 Bank Account:', instructorData.bankAccount);
            setInstructor(instructorData);
          }
        }
      } catch (err) {
        setError("ไม่สามารถโหลดข้อมูลได้");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [courseId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPaymentProof(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  // Apply discount code
  const handleApplyDiscount = async () => {
    if (!discountCode.trim() || !course || !instructor) {
      setDiscountError("กรุณากรอกรหัสส่วนลด");
      return;
    }

    setCheckingDiscount(true);
    setDiscountError("");

    try {
      // Query discount from instructor's discounts
      const discountQuery = query(
        collection(db, "discounts"),
        where("code", "==", discountCode.trim().toUpperCase()),
        where("instructorId", "==", instructor.uid)
      );
      const discountSnap = await getDocs(discountQuery);

      if (discountSnap.empty) {
        setDiscountError("ไม่พบรหัสส่วนลดนี้");
        return;
      }

      const discountDoc = discountSnap.docs[0];
      const discount = { id: discountDoc.id, ...discountDoc.data() };

      // Check if active
      if (!discount.isActive) {
        setDiscountError("รหัสส่วนลดนี้ถูกปิดใช้งานแล้ว");
        return;
      }

      // Check expiration
      const expireDate = discount.expiresAt?.toDate?.() || new Date(discount.expiresAt);
      if (expireDate < new Date()) {
        setDiscountError("รหัสส่วนลดนี้หมดอายุแล้ว");
        return;
      }

      // Calculate discount amount
      let discountValue = 0;
      if (discount.type === "percentage") {
        discountValue = Math.round((course.price * discount.value) / 100);
      } else {
        discountValue = discount.value;
      }

      // Ensure discount doesn't exceed course price
      discountValue = Math.min(discountValue, course.price);

      setAppliedDiscount(discount);
      setDiscountAmount(discountValue);
      setFinalPrice(course.price - discountValue);
    } catch (error) {
      console.error("Error applying discount:", error);
      setDiscountError("เกิดข้อผิดพลาดในการตรวจสอบรหัสส่วนลด");
    } finally {
      setCheckingDiscount(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode("");
    setDiscountAmount(0);
    setFinalPrice(course?.price || 0);
    setDiscountError("");
  };

  const handleConfirmPayment = async () => {
    if (!user || !courseId || !course) return;

    // For now, we'll add the course immediately
    // In production, you'd upload the proof and wait for instructor approval
    setConfirming(true);
    setError("");

    try {
      // Record discount usage if applied
      if (appliedDiscount) {
        await addDoc(collection(db, "discountUsages"), {
          discountId: appliedDiscount.id,
          userId: user.uid,
          userEmail: user.email || "ไม่ระบุ",
          courseId: courseId,
          courseName: course.title,
          usedAt: serverTimestamp(),
          discountAmount: discountAmount
        });
      }

      // Add course to user's purchased courses
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        purchasedCourses: arrayUnion(courseId),
      });

      // Navigate to course
      navigate(`/courses/${courseId}/learn/1`);
    } catch (err: any) {
      setError("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!course || !instructor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <Card>
          <div className="text-center py-8">
            <div className="flex items-center justify-center mb-4">
              <AlertCircle className="w-16 h-16 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-textDark mb-2">ไม่พบข้อมูล</h2>
            <p className="text-textLight">ไม่สามารถโหลดข้อมูลคอร์สได้</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/30 to-pink-50/30 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                <CheckCircle className="w-5 h-5" />
              </div>
              <span className="text-sm font-medium text-textDark">เลือกคอร์ส</span>
            </div>
            <div className="w-12 h-0.5" style={{ background: themeColor }}></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: buttonColor }}>
                2
              </div>
              <span className="text-sm font-medium" style={{ color: buttonColor }}>ชำระเงิน</span>
            </div>
            <div className="w-12 h-0.5 bg-gray-300"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-white font-bold text-sm">
                3
              </div>
              <span className="text-sm font-medium text-textLight">เริ่มเรียน</span>
            </div>
          </div>
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: headingColor }}>
              ชำระเงินคอร์ส
            </h1>
            <p className="text-textLight">
              โอนเงินผ่าน QR Code แล้วกดยืนยันเพื่อเริ่มเรียนทันที
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Course Info - Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden sticky top-8">
              {course.thumbnail && (
                <div className="relative aspect-video">
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                </div>
              )}

              <div className="p-6">
                <h3 className="text-xl font-bold text-textDark mb-3 line-clamp-2">{course.title}</h3>
                <p className="text-sm text-textLight mb-6 line-clamp-3">{course.description}</p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-textLight">ผู้สอน</span>
                    <span className="font-semibold text-textDark text-sm">
                      {instructor.name || "ผู้สอน"}
                    </span>
                  </div>
                </div>

                <div className="rounded-xl p-5 border-2" style={{ borderColor: buttonColor, background: `${buttonColor}10` }}>
                  <div className="text-center">
                    <span className="text-sm text-textLight block mb-2">ยอดชำระ</span>
                    {appliedDiscount ? (
                      <div>
                        <span className="text-2xl font-bold text-gray-400 line-through block mb-1">
                          ฿{course.price?.toLocaleString()}
                        </span>
                        <span className="text-4xl font-black" style={{ color: buttonColor }}>
                          ฿{finalPrice.toLocaleString()}
                        </span>
                        <div className="mt-2 inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                          <Tag className="w-4 h-4" />
                          ประหยัด ฿{discountAmount.toLocaleString()}
                        </div>
                      </div>
                    ) : (
                      <span className="text-4xl font-black" style={{ color: buttonColor }}>
                        ฿{course.price?.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: buttonColor }}>
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold" style={{ color: headingColor }}>สแกน QR Code เพื่อชำระเงิน</h2>
                  <p className="text-sm text-textLight">ใช้แอปธนาคารของคุณสแกน QR Code</p>
                </div>
              </div>

              {/* QR Code Display */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 mb-6">
              {instructor.paymentQRCode ? (
                <div className="text-center">
                  <div className="bg-white rounded-2xl p-8 inline-block shadow-xl">
                    <img
                      src={instructor.paymentQRCode}
                      alt="Payment QR Code"
                      className="w-72 h-72 mx-auto mb-4 rounded-xl"
                    />
                  </div>
                  <div className="mt-6 space-y-2">
                    <p className="text-lg font-bold text-textDark">
                      สแกน QR Code เพื่อชำระเงิน
                    </p>
                    <p className="text-sm text-textLight">
                      เปิดแอปธนาคารของคุณ → สแกน QR Code → ยืนยันการชำระเงิน
                    </p>
                  </div>
                  {instructor.bankAccount && (
                    <div className="mt-6 pt-6 border-t border-blue-200">
                      <p className="text-sm text-textLight mb-2">หรือโอนเงินไปที่</p>
                      <div className="bg-white rounded-xl p-4 inline-block border-2 border-blue-200">
                        <p className="text-base font-bold text-textDark">{instructor.bankAccount}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : instructor.bankAccount ? (
                <div className="text-center py-12">
                  <div className="flex items-center justify-center mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                      <CreditCard className="w-10 h-10 text-white" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-textDark mb-4">
                    โอนเงินไปที่บัญชีธนาคาร
                  </h3>
                  <div className="bg-white rounded-2xl p-8 max-w-md mx-auto border-2 border-blue-200 shadow-lg">
                    <p className="text-sm text-textLight mb-3">เลขบัญชี</p>
                    <p className="text-2xl font-black text-textDark mb-4">{instructor.bankAccount}</p>
                    <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-900">
                      <p>💡 โอนเงินตามจำนวนที่ระบุ แล้วเก็บหลักฐานการโอนไว้</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="flex items-center justify-center mb-6">
                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                      <AlertCircle className="w-10 h-10 text-blue-500" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-textDark mb-3">
                    ผู้สอนยังไม่ได้ตั้งค่าข้อมูลการชำระเงิน
                  </h3>
                  <p className="text-textLight mb-6 max-w-md mx-auto">
                    กรุณาติดต่อผู้สอนโดยตรงเพื่อขอข้อมูลการชำระเงิน<br/>
                    หรือรอให้ผู้สอนตั้งค่าข้อมูลการรับชำระเงิน
                  </p>
                  <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 max-w-md mx-auto text-left">
                    <p className="text-sm text-blue-900">
                      <strong>หมายเหตุ:</strong> ผู้สอนสามารถตั้งค่า QR Code หรือเลขบัญชีได้ที่หน้า "ตั้งค่าโปรไฟล์"
                    </p>
                  </div>
                </div>
              )}
            </div>

              {/* Upload Payment Proof */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-textDark mb-2">
                  อัปโหลด Slip การโอนเงิน (ไม่บังคับ)
                </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="block w-full text-sm text-textLight
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-xl file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-primary
                  hover:file:bg-blue-100
                  cursor-pointer"
              />
              {previewUrl && (
                <div className="mt-4">
                  <img
                    src={previewUrl}
                    alt="Payment Proof Preview"
                    className="w-full h-48 object-contain rounded-xl border-2 border-gray-200"
                  />
                </div>
              )}
            </div>

              {/* Discount Code Section */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Tag className="w-6 h-6" style={{ color: buttonColor }} />
                  <h3 className="text-lg font-bold" style={{ color: headingColor }}>รหัสส่วนลด</h3>
                </div>
                
                {appliedDiscount ? (
                  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                          <Check className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-green-900">{appliedDiscount.code}</p>
                          <p className="text-sm text-green-700">
                            {appliedDiscount.type === "percentage" 
                              ? `ลด ${appliedDiscount.value}%` 
                              : `ลด ฿${appliedDiscount.value.toLocaleString()}`}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleRemoveDiscount}
                        className="p-2 hover:bg-green-100 rounded-lg transition-all"
                        title="ลบส่วนลด"
                      >
                        <X className="w-5 h-5 text-green-700" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        placeholder="ใส่รหัสส่วนลด"
                        className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-4 focus:ring-purple-100 focus:outline-none transition-all"
                        onKeyPress={(e) => e.key === 'Enter' && handleApplyDiscount()}
                      />
                      <button
                        onClick={handleApplyDiscount}
                        disabled={checkingDiscount || !discountCode.trim()}
                        className="px-6 py-3 rounded-xl font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)` }}
                      >
                        {checkingDiscount ? "กำลังตรวจสอบ..." : "ใช้"}
                      </button>
                    </div>
                    {discountError && (
                      <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                        <X className="w-4 h-4" />
                        {discountError}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-6">
                  <p className="text-red-700 text-sm font-medium">{error}</p>
                </div>
              )}

              {/* Confirm Button */}
              <button
                onClick={handleConfirmPayment}
                disabled={confirming}
                className="relative w-full text-white py-5 rounded-xl font-bold text-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none overflow-hidden group"
                style={{ background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700"></div>
                {confirming ? (
                  <span className="relative flex items-center justify-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    กำลังดำเนินการ...
                  </span>
                ) : (
                  <span className="relative flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    <span>ยืนยันการชำระเงินและเริ่มเรียนเลย</span>
                  </span>
                )}
              </button>

              <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Lightbulb className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  <div className="text-sm text-blue-900">
                    <p className="font-semibold mb-1">ขั้นตอนสุดท้าย!</p>
                    <p>โอนเงินตามจำนวนที่ระบุ แล้วกดปุ่มด้านบนเพื่อเข้าเรียนทันที</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center">
                  <ClipboardList className="w-10 h-10" style={{ color: themeColor }} />
                </div>
                <div>
                  <h3 className="font-bold mb-3" style={{ color: headingColor }}>ข้อมูลสำคัญ</h3>
                  <ul className="text-sm text-textLight space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>โอนเงินตามจำนวนที่ระบุให้ครบถ้วน</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>เก็บหลักฐานการโอนเงินไว้เป็นหลักฐาน</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>หากมีปัญหาการชำระเงิน กรุณาติดต่อผู้สอนโดยตรง</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>คุณจะได้รับสิทธิ์เข้าเรียนทันทีหลังยืนยันการชำระเงิน</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
